import { getUser } from '../stores'
import { ChatLocalMesage, ChatUser, MessageGrouping, MessageStatusTypes, QuickReplyDraft, QuotedMessage } from '../types'
import type { LocalAttachment, Attachment as RemoteAttachment, StreamChat } from 'stream-chat'
import { attachmentPreviewText, getAttachmentExtension, getAttachmentLabel, getAttachmentFallbackText } from './attachments'
import { Constants } from '../constants'
import { removeDuplicated } from './misc'

type Attachment = LocalAttachment | RemoteAttachment

type GetCustomMessageTextParams = {
  text?: string
  attachments?: Attachment[]
  pollName?: string
}

const getCustomMessageText = ({ text, attachments = [], pollName }: GetCustomMessageTextParams) => {
  const originalText = text || ''
  const messageText = originalText.trim()
  const attachment = attachments?.[0]

  if (pollName) {
    return `📊 ${pollName}`
  }

  if (messageText && attachment) {
    return `${getAttachmentLabel(attachment)} ${messageText}`
  }

  if (messageText) return messageText
  if (attachment) return getAttachmentFallbackText(attachment)

  return 'New message'
}

export type GetCustomMessageDataParams = GetCustomMessageTextParams & {
  mentionedUsers?: ChatUser[] | string[]
}

function getCustomMentionedUsers(mentionedUsers: string[]) {
  const filteredMentionedUsers = removeDuplicated(mentionedUsers)

  const mentionedUsersObject = filteredMentionedUsers.reduce((acc, userId, index) => {
    acc[`mentioned_user_${index + 1}`] = userId
    return acc
  }, {} as Record<string, string>)

  return mentionedUsersObject
} 

export const getCustomMessageData = (props: GetCustomMessageDataParams) => {

  const data = { 
    customMessage: getCustomMessageText(props), 
    ...(props.mentionedUsers?.length && getCustomMentionedUsers(props.mentionedUsers?.map(user => typeof user === 'string' ? user : user.id))),
  }
  return data
}

export function isMessageDeleted(message?: Pick<ChatLocalMesage, 'type'>) {
  if (message?.type === 'deleted') return true
}

export function isSystemMessage(message?: Pick<ChatLocalMesage, 'type'>) {
  if (message?.type === 'system') return true
}

type Action = {
  text: string
  rightIcon: string
  // `style` accepts either an array of action-level class names (e.g.
  // `['list:destructive']` for the delete action) or a nested per-slot style
  // object (e.g. `{ rightIcon: { transform: [{ scaleX: -1 }] } }` for the
  // Quick Reply mirror flip). Different downstream renderers handle each.
  style?: string[] | Record<string, unknown>
}

const actionsMap = {
  // "Threaded reply" opens the Stream thread for this message — the historical
  // "Reply" label is renamed to disambiguate from Quick Reply (FR-0077), which
  // quotes the message in place without entering a thread.
  threadedReply: {
    text: 'Threaded reply',
    rightIcon: 'message-text-circle-02',
  },
  forward: {
    text: 'Forward',
    rightIcon: 'share-06',
  },
  // @spec FR-0077/AC-10@v1 — long-press → Quick reply (accessible alternative to swipe).
  quickReply: {
    text: 'Quick reply',
    rightIcon: 'share-06',
    style: { rightIcon: { transform: [{ scaleX: -1 }] } },
  },
  reminder: {
    text: 'Set reminder',
    rightIcon: 'clock-stopwatch',
  },
  edit: {
    text: 'Edit',
    rightIcon: 'pencil-line',
  },
  pin: {
    text: 'Pin',
    rightIcon: 'pin-01',
  },
  unpin: {
    text: 'Unpin',
    rightIcon: 'pin-blocked-02',
  },
  copy: {
    text: 'Copy',
    rightIcon: 'copy-05',
  },
  delete: {
    text: 'Delete',
    rightIcon: 'trash-03',
    style: ['list:destructive'],
  },
}

export type MessageActions = keyof typeof actionsMap

type ActionItem = Action & { id: MessageActions }

const ALL_ACTIONS: ActionItem[] = Object.entries(actionsMap).map(([id, action]) => ({
  id: id as MessageActions,
  ...action,
}))

export type GetMessageActionsProps = {
  isMyMessage: boolean
  isMessagePinned?: boolean
  isChatAdmin?: boolean
  isChatGroup?: boolean
  isThreadParent?: boolean
  message: ChatLocalMesage
}

export function getMessageActions({isMyMessage, isChatAdmin, isMessagePinned, isChatGroup, isThreadParent, message }: GetMessageActionsProps) {
  let actions = ALL_ACTIONS
  const hasText = !!message?.text?.trim()
  const isThread = isThreadMessage(message)
  const isPoll = isPollMessage(message)

  function removeAction(actionsIds: MessageActions[]) {
    const newActions =  actions.filter(action => !actionsIds.includes(action.id))
    actions = newActions
  }

  if (isPoll) {
    removeAction(['forward', 'edit', 'copy'])
  }

  if (isThread || isThreadParent) {
    removeAction(['threadedReply'])
  }

  if (isThreadParent) {
    removeAction(['delete'])
  }

  if (isMessageForwarded(message)) {
    removeAction(['edit'])
  }

  if (!hasText) {
    removeAction(['edit', 'copy'])
  }

  if (isMessagePinned) {
    removeAction(['pin'])
  } else {
    removeAction(['unpin'])
  }

  if (isChatGroup && !isChatAdmin) {
    removeAction(['unpin', 'pin'])
  }
    
  if (!isMyMessage) {
    removeAction(['edit', 'delete'])
  }

  return actions
}

export function splitAggregatedSystemMessage(message: ChatLocalMesage): ChatLocalMesage[] {
  // @ts-ignore
  const targets = Array.isArray(message?.custom?.targets) ? message.custom.targets : []
  if (targets.length < 1) return [message]

  // @ts-ignore
  const action = message?.custom?.action
  const defaultTextByAction: Record<string, string> = {
    members_added: 'added',
    moderators_granted: 'granted admin to',
  }

  return targets.map((target: { id?: string, name?: string }) => {
    // @ts-ignore
    const custom = {
      // @ts-ignore
      ...(message?.custom || {}),
      target_id: target?.id,
      target_name: target?.name,
    }

    delete custom.targets
    delete custom.count

    return {
      ...message,
      text: defaultTextByAction[action] || message?.text,
      custom,
    } as ChatLocalMesage
  })
}

export function messageSystemText(message: ChatLocalMesage) {
  // If this is an aggregated system message, preview should match
  // the last generated split message so chat list text mirrors UI ordering.
  // @ts-ignore
  const hasTargets = Array.isArray(message?.custom?.targets) && message.custom.targets.length > 0
  if (hasTargets) {
    const splitMessages = splitAggregatedSystemMessage(message)
    const lastGeneratedMessage = splitMessages[splitMessages.length - 1]
    if (lastGeneratedMessage) {
      return messageSystemText(lastGeneratedMessage)
    }
  }

  // @ts-ignore
  const targetId = message?.custom?.target_id
  // @ts-ignore
  const targetName = message?.custom?.target_name
  const user = getUser()
  const userName = message?.user?.id === user?.id ? 'You' : message?.user?.name || 'Someone'
  const targetUserName = targetId ? ` ${targetId === user?.id ? 'You' : targetName}` : ''
  const messageText = `${userName} ${message?.text}${targetUserName}`
  return messageText
}

type UseMessageGroupingProps = {
  message: ChatLocalMesage
  groupStyles: string[]
  lastGroupMessage?: boolean
}

export function useMessageGrouping({ message, groupStyles = [], lastGroupMessage: lastGroupMessageProp }: UseMessageGroupingProps): MessageGrouping {
  const isDeleted = isMessageDeleted(message)

  if (isDeleted) {
    return {
      lastGroupMessage: true,
      firstGroupMessage: true,
    }
  }

  return {
    lastGroupMessage: lastGroupMessageProp || groupStyles?.some(style => ['bottom', 'single'].includes(style)),
    firstGroupMessage: !groupStyles ? false : groupStyles?.some(style => ['top', 'single'].includes(style))
  }
}

export function isMessageEdited(message: ChatLocalMesage) {
  return !!message?.message_text_updated_at
}

export function isMessageForwarded(message: ChatLocalMesage) {
  // @ts-ignore
  return !!message?.custom?.forwarded
}

export function isThreadMessage(message: ChatLocalMesage) {
  return !!message?.parent_id
}

export function isThreadParentMessage(message: ChatLocalMesage, threadList: boolean) {
  return  threadList && !message?.parent_id
}

export type ShouldShowMessageCheckboxProps = {
  message: ChatLocalMesage
  selectMode: 'forward' | 'delete' | null
  selectModeActive: boolean
  isMyMessage: boolean
  threadList: boolean
}

// Decides whether to render the per-message Checkbox in select mode. Pure
// over the inputs so the multi-mode rule (forward vs delete) is testable
// without standing up Stream + nanostore in a UI harness.
// Regression note: the thread-parent branch is intentionally gated on
// `selectMode === 'delete'`. An earlier guard suppressed the checkbox for
// thread parents in EVERY select mode, which hid the forward checkbox on
// the ThreadHead even though forwarding the parent is a valid action;
// only delete keeps the parent unselectable (its own UX path handles it).
export function shouldShowMessageCheckbox({
  message,
  selectMode,
  selectModeActive,
  isMyMessage,
  threadList,
}: ShouldShowMessageCheckboxProps): boolean {
  if (!selectModeActive) return false
  if (isMessageDeleted(message)) return false
  if (isThreadParentMessage(message, threadList) && selectMode === 'delete') return false
  if (isPollMessage(message) && selectMode === 'forward') return false
  if (selectMode === 'delete' && !isMyMessage) return false
  return true
}

export async function getFreshMessage(client: StreamChat, message: ChatLocalMesage) {
  if (message.status !== MessageStatusTypes.SENDING) return message

  const updatedMessageResponse = await client.getMessage(message.id)
  const finalMessage = updatedMessageResponse.message
  const freshMessage = {
    ...finalMessage,
    created_at: new Date(finalMessage.created_at as string),
    updated_at: new Date(finalMessage.updated_at as string),
    deleted_at: finalMessage.deleted_at ? new Date(finalMessage.deleted_at as string) : undefined,
    pinned_at: finalMessage.pinned_at ? new Date(finalMessage.pinned_at as string) : undefined,
  } as unknown as ChatLocalMesage

  return freshMessage
}

export function isPollMessage(message: ChatLocalMesage) {
  return !!message?.poll_id
}

export function normalizeMessageText(input: string): string | null {
  const trimmed = input?.trim()
  if (!trimmed) return null

  const isOnlyMarkdown = /^[\s\-_*]+$/.test(trimmed)

  if (isOnlyMarkdown) {
    return `\u200B${trimmed}`
  }

  return trimmed
}

export function pinnedMessagePreviewText(message: ChatLocalMesage) {
  const attachment = message?.attachments?.[0]
  const hasText = !!message?.text?.trim()
  const poll = message?.poll

  if (poll) {
    return poll.name
  }

  if (hasText) {
    return message?.text 
  }

  if (attachment) return attachmentPreviewText(attachment)
  return null
}

export function messageHasAudio(message: ChatLocalMesage) {
  return message?.attachments?.some(
    attachment => attachment?.type === 'audio' || attachment?.type === 'voiceRecording',
  )
}

export function getMessageUserName(message: ChatLocalMesage) {
  return message?.user?.name || 'Unknown User'
}

/**
 * Permissive URL detector used by message renderers (bubble + quoted preview).
 * Matches optional `http(s)://` or `www.`, then host + TLD, then path/query.
 * Mirrors the historical regex that MessageText.tsx used before this lived in
 * the shared layer — extracted so the App's bubble and the Quick Reply preview
 * can highlight URLs with identical rules.
 */
export const MESSAGE_URL_REGEX = /(https?:\/\/)?(www\.)?[\w\-]+\.[a-z]{2,}([\/\w\-.?=&%#:~+]*)?/gi

// @spec BR-0029@v1 — Quoted reply preview format.
export type QuotedPreviewModel = {
  senderName: string
  isCurrentUser: boolean
  messageId: ChatLocalMesage['id']
} & (
  | { kind: 'text'; text: string }
  | { kind: 'image'; thumbnailUrl: string | null; caption?: string; count: number }
  | { kind: 'voice'; durationLabel: string }
  | { kind: 'poll'; title: string }
  | { kind: 'document'; filename: string; extension: string }
  | { kind: 'gif'; thumbnailUrl: string | null; caption?: string }
  | { kind: 'deleted' }
)

function truncateForPreview(input: string, limit: number): string {
  const trimmed = (input ?? '').trim()
  if (trimmed.length <= limit) return trimmed
  return `${trimmed.slice(0, limit)}…`
}

// Caption text alongside an image/GIF attachment, truncated like the BR-0029
// text preview so longer captions don't bloat the bar. Returns undefined when
// the message has no caption so renderers can fall back to the "Photo" / "GIF"
// label.
function messageCaption(message: QuotedMessage): string | undefined {
  const trimmed = message.text?.trim()
  if (!trimmed) return undefined
  return truncateForPreview(trimmed, Constants.quickReply.previewTextLimit)
}

// Giphy attachments sent via Connect's picker carry `custom.giphy_id` instead
// of `thumb_url`/`image_url` (see MessagesServices.sendGiphy). When the URL
// fields are empty we synthesise the GIF URL from the giphy_id so the quoted
// preview still shows a thumbnail; falling back to the same pattern Stream's
// GIPHY renderer uses internally.
function giphyThumbnailUrl(attachment: any): string | null {
  if (attachment?.thumb_url) return attachment.thumb_url
  if (attachment?.image_url) return attachment.image_url
  const giphyId = attachment?.custom?.giphy_id
  if (giphyId) return `https://i.giphy.com/${giphyId}.gif`
  return null
}

// BR-0029 example uses "0:32" — drop minute padding so single-digit minutes
// don't show as "00:32". formatPlaybackTime (attachments.ts) keeps the padded
// form for the audio scrubber; the quoted preview uses this shorter form.
function formatQuotedVoiceDuration(durationSeconds: number | undefined | null): string {
  const safe = Math.max(0, Math.floor(durationSeconds ?? 0))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// @spec FR-0077/AC-2@v1 — sender name + content preview.
// @spec FR-0077/AC-9@v1 — reply-to-a-reply surfaces only the immediate parent.
// @spec BR-0029@v1 — Quoted reply preview format.
export function getQuotedPreview(message: QuotedMessage | undefined | null): QuotedPreviewModel | null {
  if (!message) return null

  const currentUser = getUser()
  const isCurrentUser = !!currentUser && message?.user?.id === currentUser.id

  const base = {
    senderName: getMessageUserName(message),
    isCurrentUser,
    messageId: message.id,
  }

  // BR-0031 is handled by the renderer reading isQuotedOriginalDeleted on the
  // live channel state. We surface the deleted kind here so callers that only
  // hold a snapshot (e.g. the composer preview) can render the deleted state
  // without a second lookup.
  if (isQuotedOriginalDeleted(message)) {
    return { ...base, kind: 'deleted' }
  }

  if (message.poll_id) {
    const pollTitle = message.poll?.name ?? message.pollName ?? ''
    return { ...base, kind: 'poll', title: truncateForPreview(pollTitle, Constants.quickReply.previewTextLimit) }
  }

  const attachment = message.attachments?.[0]
  if (attachment) {
    switch (attachment.type) {
      case 'voiceRecording':
      case 'audio':
        return {
          ...base,
          kind: 'voice',
          durationLabel: formatQuotedVoiceDuration(attachment.duration),
        }
      case 'giphy':
        return {
          ...base,
          kind: 'gif',
          thumbnailUrl: giphyThumbnailUrl(attachment),
          caption: messageCaption(message),
        }
      case 'image': {
        const imageCount = (message.attachments ?? []).reduce(
          (n, att) => att?.type === 'image' ? n + 1 : n,
          0,
        )
        return {
          ...base,
          kind: 'image',
          thumbnailUrl: imageCount > 1
            ? null
            : (attachment.thumb_url ?? attachment.image_url ?? null),
          caption: imageCount > 1 ? undefined : messageCaption(message),
          count: imageCount,
        }
      }
      case 'file':
        return {
          ...base,
          kind: 'document',
          filename: attachment.title ?? '',
          extension: getAttachmentExtension(attachment),
        }
    }
  }

  // Default: text. Truncate at 100 chars (BR-0029) and let the renderer add
  // the ellipsis via CSS/RN when the bubble can't fit it; we still include the
  // ellipsis character here so plain-text consumers (a11y, notifications) get
  // a faithful preview.
  return {
    ...base,
    kind: 'text',
    text: truncateForPreview(message.text ?? '', Constants.quickReply.previewTextLimit),
  }
}

// @spec BR-0030@v1 — empty-send guard.
export function canSendQuickReply(
  draft: Pick<QuickReplyDraft, 'text' | 'attachments'>,
): boolean {
  const hasText = !!draft?.text?.trim()
  const hasAttachment = !!draft?.attachments?.length
  return hasText || hasAttachment
}

// @spec BR-0031@v1 — Quoted preview when original is deleted.
export function isQuotedOriginalDeleted(
  quoted: Pick<ChatLocalMesage, 'type' | 'deleted_at'> | undefined | null,
): boolean {
  if (!quoted) return false
  if (quoted.type === 'deleted') return true
  if (quoted.deleted_at) return true
  return false
}
